import React, { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ServiceType } from "@/context/LeadContext";
import { useToast } from "@/components/ui/use-toast";
import { useOptionalPartners } from "@/context/PartnerContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import {
  BellOff,
  Building2,
  CheckCircle2,
  Image,
  Loader2,
  Mail,
  MapPin,
  MapPinned,
  Save,
  Wrench,
} from "lucide-react";
import { loadGoogleMapsScript } from "@/utils/loadGoogleMapsScript";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import AddressAutocomplete from "@/components/ui-custom/AddressAutocomplete";
import ThankYouMessage from "@/components/ui-custom/ThankYouMessage";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { SERVICE_OPTIONS } from "@/lib/serviceCatalog";

const formSchema = z.object({
  name: z.string().trim().min(3, {
    message: "Το όνομα πρέπει να είναι τουλάχιστον 3 χαρακτήρες",
  }).max(255, { message: "Το όνομα είναι πολύ μεγάλο" }),
  phone: z.string().trim().regex(/^[0-9+() .-]{10,30}$/, {
    message: "Παρακαλώ εισάγετε έγκυρο αριθμό τηλεφώνου",
  }),
  email: z.string().trim().email({
    message: "Παρακαλώ εισάγετε έγκυρο email",
  }).or(z.literal("")),
  location: z.string().trim().min(3, {
    message: "Παρακαλώ εισάγετε τοποθεσία",
  }).max(255, { message: "Η τοποθεσία είναι πολύ μεγάλη" }),
  miniImageUrl: z.string().trim().max(500, { message: "Το URL εικόνας είναι πολύ μεγάλο" }).refine(
    (value) => value === "" || /^https?:\/\/[^\s]+$/i.test(value),
    { message: "Χρησιμοποιήστε έγκυρο http(s) URL" },
  ).optional(),
  miniDescription: z.string().max(500, {
    message: "Η περιγραφή μπορεί να έχει έως 500 χαρακτήρες",
  }).optional(),
  services: z.array(z.string()).min(1, {
    message: "Επιλέξτε τουλάχιστον μία υπηρεσία",
  }),
  active: z.boolean().default(true),
  sms_opt_out: z.boolean().default(false), // Add the SMS opt-out field
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  radius: z.number().min(1).max(100).default(10),
});

export type PartnerFormValues = z.infer<typeof formSchema>;

interface PartnerFormProps {
  onSuccess?: () => void;
  initialData?: {
    id?: string;
    name: string;
    phone: string;
    email: string;
    location: string;
    miniImageUrl?: string;
    miniDescription?: string;
    services: string[];
    active: boolean;
    sms_opt_out: boolean;
    latitude?: number | null;
    longitude?: number | null;
    radius?: number;
  };
  isAdminView?: boolean;
  publicSubmit?: (data: PartnerFormValues) => Promise<void>;
  submitPartner?: (data: PartnerFormValues) => Promise<void>;
  submitLabel?: string;
}

const PartnerForm: React.FC<PartnerFormProps> = ({
  onSuccess,
  initialData,
  isAdminView = false,
  publicSubmit,
  submitPartner,
  submitLabel,
}) => {
  const { toast } = useToast();
  const partnerContext = useOptionalPartners();
  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);
  
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [mapsError, setMapsError] = useState<string | null>(null);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  
  const defaultValues = {
    name: initialData?.name || "",
    phone: initialData?.phone || "",
    email: initialData?.email || "",
    location: initialData?.location || "",
    miniImageUrl: initialData?.miniImageUrl || "",
    miniDescription: initialData?.miniDescription || "",
    services: initialData?.services || [],
    active: initialData?.active !== undefined ? Boolean(initialData.active) : true,
    sms_opt_out: initialData?.sms_opt_out !== undefined ? Boolean(initialData.sms_opt_out) : false, // Initialize SMS opt-out field
    latitude: initialData?.latitude,
    longitude: initialData?.longitude,
    radius: initialData?.radius || 10,
  };
  
  const form = useForm<PartnerFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  useEffect(() => {
    const initMaps = async () => {
      try {
        await loadGoogleMapsScript();
        setMapsLoaded(true);
        window.requestAnimationFrame(initializeMap);
      } catch (error) {
        console.error("Failed to load Google Maps:", error);
        setMapsError("Ο χάρτης δεν φορτώθηκε. Μπορείτε να αποθηκεύσετε τα υπόλοιπα στοιχεία και να δοκιμάσετε ξανά αργότερα.");
        toast({
          title: "Σφάλμα φόρτωσης χάρτη",
          description: "Δεν ήταν δυνατή η φόρτωση του χάρτη Google Maps.",
          variant: "destructive"
        });
      }
    };
    
    initMaps();
  // The editor is mounted with one immutable initial partner snapshot; later
  // server refreshes reset the form instead of recreating the Maps instance.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast]);

  useEffect(() => {
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      if (!form.formState.isDirty || form.formState.isSubmitting) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [form.formState.isDirty, form.formState.isSubmitting]);

  const initializeMap = () => {
    if (!mapRef.current || !window.google || !window.google.maps) return;
    
    const center = { lat: 38.9, lng: 22.9 };
    
    if (initialData?.latitude && initialData?.longitude) {
      center.lat = initialData.latitude;
      center.lng = initialData.longitude;
    }
    
    const mapOptions: google.maps.MapOptions = {
      center,
      zoom: initialData?.latitude ? 10 : 7,
      styles: [],
      mapTypeControl: false,
      streetViewControl: false
    };
    
    googleMapRef.current = new window.google.maps.Map(mapRef.current, mapOptions);
    
    if (initialData?.latitude && initialData?.longitude) {
      const position = { lat: initialData.latitude, lng: initialData.longitude };
      
      const markerOptions: google.maps.MarkerOptions = {
        position,
        map: googleMapRef.current,
        draggable: true
      };
      
      markerRef.current = new window.google.maps.Marker(markerOptions);
      
      circleRef.current = new window.google.maps.Circle({
        strokeColor: "#1E88E5",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#1E88E5",
        fillOpacity: 0.1,
        map: googleMapRef.current,
        center: position,
        radius: (initialData.radius || 10) * 1000,
      });
      
      if (markerRef.current) {
        google.maps.event.addListener(markerRef.current, 'dragend', function() {
          const pos = markerRef.current!.getPosition();
          if (pos) {
            form.setValue('latitude', pos.lat(), { shouldDirty: true, shouldValidate: true });
            form.setValue('longitude', pos.lng(), { shouldDirty: true, shouldValidate: true });
            
            if (circleRef.current) {
              circleRef.current.setCenter(pos);
            }
          }
        });
      }
    }
    
    if (googleMapRef.current) {
      google.maps.event.addListener(googleMapRef.current, 'click', function(event) {
        placeMarker(event.latLng);
      });
    }
  };

  const placeMarker = (location: google.maps.LatLng) => {
    if (!window.google || !window.google.maps || !googleMapRef.current) return;
    
    form.setValue('latitude', location.lat(), { shouldDirty: true, shouldValidate: true });
    form.setValue('longitude', location.lng(), { shouldDirty: true, shouldValidate: true });
    
    if (markerRef.current) {
      markerRef.current.setPosition(location);
    } else {
      const markerOptions: google.maps.MarkerOptions = {
        position: location,
        map: googleMapRef.current,
        draggable: true
      };
      
      markerRef.current = new window.google.maps.Marker(markerOptions);
      
      if (markerRef.current) {
        google.maps.event.addListener(markerRef.current, 'dragend', function() {
          const pos = markerRef.current!.getPosition();
          if (pos) {
            form.setValue('latitude', pos.lat(), { shouldDirty: true, shouldValidate: true });
            form.setValue('longitude', pos.lng(), { shouldDirty: true, shouldValidate: true });
            
            if (circleRef.current) {
              circleRef.current.setCenter(pos);
            }
          }
        });
      }
    }
    
    const radiusInMeters = (form.getValues('radius') || 10) * 1000;
    
    if (circleRef.current) {
      circleRef.current.setCenter(location);
      circleRef.current.setRadius(radiusInMeters);
    } else {
      circleRef.current = new window.google.maps.Circle({
        strokeColor: "#1E88E5",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#1E88E5",
        fillOpacity: 0.1,
        map: googleMapRef.current,
        center: location,
        radius: radiusInMeters,
      });
    }
  };

  const updateRadius = (value: number[]) => {
    const radius = value[0];
    form.setValue('radius', radius, { shouldDirty: true, shouldValidate: true });
    
    if (circleRef.current) {
      circleRef.current.setRadius(radius * 1000);
    }
  };

  const handleLocationSelect = (lat: number, lng: number) => {
    if (lat && lng) {
      form.setValue('latitude', lat, { shouldDirty: true, shouldValidate: true });
      form.setValue('longitude', lng, { shouldDirty: true, shouldValidate: true });
      
      const position = new google.maps.LatLng(lat, lng);
      
      if (googleMapRef.current) {
        googleMapRef.current.setCenter(position);
        googleMapRef.current.setZoom(12);
      }
      
      placeMarker(position);
    }
  };

  const onSubmit = async (data: PartnerFormValues) => {
    try {
      if (submitPartner) {
        await submitPartner(data);
        form.reset(data);
      } else if (initialData?.id) {
        if (!partnerContext) throw new Error('Admin partner context unavailable');
        await partnerContext.updatePartner(initialData.id, {
          name: data.name,
          phone: data.phone,
          email: (data.email || "").trim() || "",
          location: data.location,
          miniImageUrl: (data.miniImageUrl || "").trim(),
          miniDescription: (data.miniDescription || "").trim(),
          services: data.services as ServiceType[],
          active: data.active,
          sms_opt_out: data.sms_opt_out, // Include SMS opt-out in update
          latitude: data.latitude ?? undefined,
          longitude: data.longitude ?? undefined,
          radius: data.radius
        });
        form.reset(data);
      } else {
        if (publicSubmit) {
          await publicSubmit(data);
        } else {
          if (!partnerContext) throw new Error('Admin partner context unavailable');
          await partnerContext.addPartner({
          name: data.name,
          phone: data.phone,
          email: (data.email || "").trim() || "",
          location: data.location,
          miniImageUrl: (data.miniImageUrl || "").trim() || undefined,
          miniDescription: (data.miniDescription || "").trim() || undefined,
          services: data.services as ServiceType[],
          active: isAdminView ? data.active : true,
          sms_opt_out: isAdminView ? data.sms_opt_out : false, // Include SMS opt-out in add
          latitude: data.latitude ?? undefined,
          longitude: data.longitude ?? undefined,
          radius: data.radius
          });
        }
        form.reset({
          name: "",
          phone: "",
          email: "",
          location: "",
          miniImageUrl: "",
          miniDescription: "",
          services: [],
          active: true,
          sms_opt_out: false,
          radius: 10
        });
        
        if (markerRef.current) {
          markerRef.current.setMap(null);
          markerRef.current = null;
        }
        
        if (circleRef.current) {
          circleRef.current.setMap(null);
          circleRef.current = null;
        }
      }
      
      setRegistrationComplete(true);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Partner form error:", error);
      const fieldErrors = error && typeof error === "object" && "fields" in error
        ? (error as { fields?: Record<string, string> }).fields || {}
        : {};
      const fieldMap: Record<string, keyof PartnerFormValues> = {
        smsOptOut: "sms_opt_out",
        miniImageUrl: "miniImageUrl",
        miniDescription: "miniDescription",
        name: "name",
        phone: "phone",
        email: "email",
        location: "location",
        services: "services",
        active: "active",
        latitude: "latitude",
        longitude: "longitude",
        radius: "radius",
      };
      Object.entries(fieldErrors).forEach(([key, message]) => {
        const field = fieldMap[key];
        if (field) form.setError(field, { type: "server", message });
      });
      toast({
        title: "Δεν αποθηκεύτηκε",
        description: error instanceof Error ? error.message : "Υπήρξε πρόβλημα. Παρακαλώ δοκιμάστε ξανά.",
        variant: "destructive",
      });
    }
  };

  const services = SERVICE_OPTIONS.map(({ value, label }) => ({ id: value, label }));

  // If registration is complete, show thank you message
  if (registrationComplete && !isAdminView) {
    return <ThankYouMessage serviceName="Συνεργάτη" />;
  }

  const selectedLatitude = form.watch("latitude");
  const selectedLongitude = form.watch("longitude");
  const previewImage = form.watch("miniImageUrl");
  const isSubmitting = form.formState.isSubmitting;
  const isDirty = form.formState.isDirty;
  const isCreate = isAdminView && !initialData?.id;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, () => {
          toast({
            title: "Δεν αποθηκεύτηκε",
            description: "Ελέγξτε τα πεδία με κόκκινο μήνυμα και δοκιμάστε ξανά.",
            variant: "destructive",
          });
        })}
        className="space-y-5"
      >
        {isAdminView && (
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="active"
              render={({ field }) => (
                <FormItem className={`flex min-h-[76px] items-center justify-between gap-4 rounded-2xl border p-4 transition-colors ${field.value ? "border-emerald-200 bg-emerald-50/70" : "border-slate-200 bg-slate-50"}`}>
                  <div className="min-w-0">
                    <FormLabel htmlFor="partner-active" className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-950">
                      <CheckCircle2 className={`h-4 w-4 ${field.value ? "text-emerald-700" : "text-slate-400"}`} />
                      Routing συνεργάτη
                    </FormLabel>
                    <p className="mt-1 text-xs leading-5 text-slate-600">{field.value ? "Ενεργός και διαθέσιμος για νέα leads" : "Ανενεργός — δεν θα λαμβάνει νέα leads"}</p>
                  </div>
                  <FormControl><Switch id="partner-active" checked={field.value} onCheckedChange={field.onChange} aria-label="Ενεργός συνεργάτης" className="relative after:absolute after:-inset-x-1 after:-inset-y-2.5 after:content-['']" /></FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="sms_opt_out"
              render={({ field }) => (
                <FormItem className={`flex min-h-[76px] items-center justify-between gap-4 rounded-2xl border p-4 transition-colors ${field.value ? "border-amber-200 bg-amber-50/70" : "border-sky-200 bg-sky-50/60"}`}>
                  <div className="min-w-0">
                    <FormLabel htmlFor="partner-sms-opt-out" className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-950"><BellOff className={`h-4 w-4 ${field.value ? "text-amber-700" : "text-sky-700"}`} /> SMS ειδοποιήσεις</FormLabel>
                    <p className="mt-1 text-xs leading-5 text-slate-600">{field.value ? "Καταγεγραμμένο opt-out — μόνο email" : "Ενεργές ειδοποιήσεις SMS"}</p>
                  </div>
                  <FormControl><Switch id="partner-sms-opt-out" checked={!field.value} onCheckedChange={(checked) => field.onChange(!checked)} aria-label="Ενεργές ειδοποιήσεις SMS" className="relative after:absolute after:-inset-x-1 after:-inset-y-2.5 after:content-['']" /></FormControl>
                </FormItem>
              )}
            />
          </div>
        )}

        <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1.08fr)_minmax(360px,0.92fr)]">
          <div className="space-y-5">
            <Card className="rounded-2xl border-slate-200 shadow-sm">
              <CardHeader className="p-4 pb-0 sm:p-5 sm:pb-0">
                <CardTitle className="flex items-center gap-2 text-base text-slate-950"><Building2 className="h-4 w-4 text-sky-700" /> Στοιχεία επικοινωνίας</CardTitle>
                <p className="text-sm leading-6 text-slate-500">Τα στοιχεία που χρησιμοποιούνται για ανάθεση και ειδοποιήσεις.</p>
              </CardHeader>
              <CardContent className="grid gap-4 p-4 sm:grid-cols-2 sm:p-5">
                <FormField control={form.control} name="name" render={({ field }) => (
                  <FormItem className="sm:col-span-2"><FormLabel>Όνομα συνεργάτη</FormLabel><FormControl><Input className="h-11" autoComplete="organization" placeholder="Εταιρεία ή επαγγελματίας" {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="phone" render={({ field }) => (
                  <FormItem><FormLabel>Τηλέφωνο</FormLabel><FormControl><Input className="h-11" type="tel" inputMode="tel" autoComplete="tel" placeholder="π.χ. 6912345678" {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="email" render={({ field }) => (
                  <FormItem><FormLabel>Email</FormLabel><FormControl><div className="relative"><Input className="h-11 pr-10" type="email" autoComplete="email" placeholder="info@example.gr" {...field} /><Mail className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /></div></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="location" render={({ field }) => (
                  <FormItem className="sm:col-span-2"><FormLabel>Έδρα / περιοχή</FormLabel><FormControl><div className="relative"><AddressAutocomplete value={field.value} onChange={field.onChange} onLocationSelect={handleLocationSelect} placeholder="Διεύθυνση, πόλη ή ΤΚ" className="h-11 pr-10" /><MapPin className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /></div></FormControl><p className="text-xs leading-5 text-slate-500">Επιλέξτε πρόταση Google για να ενημερωθεί με ακρίβεια το σημείο του χάρτη.</p><FormMessage /></FormItem>
                )} />
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-slate-200 shadow-sm">
              <CardHeader className="p-4 pb-0 sm:p-5 sm:pb-0"><CardTitle className="flex items-center gap-2 text-base text-slate-950"><Wrench className="h-4 w-4 text-sky-700" /> Υπηρεσίες routing</CardTitle><p className="text-sm leading-6 text-slate-500">Ο συνεργάτης εξετάζεται για lead μόνο στις επιλεγμένες υπηρεσίες.</p></CardHeader>
              <CardContent className="p-4 sm:p-5">
                <FormField control={form.control} name="services" render={({ field }) => (
                  <FormItem>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {services.map((service) => {
                        const checked = field.value?.includes(service.id);
                        const checkboxId = `partner-service-${service.id}`;
                        return (
                          <label key={service.id} htmlFor={checkboxId} className={`flex min-h-12 cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-2.5 text-sm font-medium transition focus-within:ring-2 focus-within:ring-sky-600 focus-within:ring-offset-2 ${checked ? "border-sky-300 bg-sky-50 text-sky-950" : "border-slate-200 bg-white text-slate-700 hover:border-sky-200 hover:bg-slate-50"}`}>
                            <Checkbox id={checkboxId} checked={checked} onCheckedChange={(value) => field.onChange(value ? [...field.value, service.id] : field.value.filter((item) => item !== service.id))} />
                            <span>{service.label}</span>
                          </label>
                        );
                      })}
                    </div>
                    <FormMessage className="mt-2" />
                  </FormItem>
                )} />
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-slate-200 shadow-sm">
              <CardHeader className="p-4 pb-0 sm:p-5 sm:pb-0"><CardTitle className="flex items-center gap-2 text-base text-slate-950"><Image className="h-4 w-4 text-sky-700" /> Mini page</CardTitle><p className="text-sm leading-6 text-slate-500">Προαιρετικό περιεχόμενο για τη δημόσια κάρτα του συνεργάτη.</p></CardHeader>
              <CardContent className="grid gap-4 p-4 sm:p-5 lg:grid-cols-[minmax(0,1fr)_150px]">
                <div className="space-y-4">
                  <FormField control={form.control} name="miniImageUrl" render={({ field }) => (
                    <FormItem><FormLabel>URL εικόνας</FormLabel><FormControl><Input className="h-11" inputMode="url" placeholder="https://example.gr/partner.jpg" {...field} value={field.value || ""} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="miniDescription" render={({ field }) => (
                    <FormItem><div className="flex items-center justify-between gap-3"><FormLabel>Σύντομη περιγραφή</FormLabel><span className="text-xs tabular-nums text-slate-400">{(field.value || "").length}/500</span></div><FormControl><Textarea className="min-h-28 resize-y" maxLength={500} placeholder="Σύντομη παρουσίαση συνεργάτη…" {...field} value={field.value || ""} /></FormControl><FormMessage /></FormItem>
                  )} />
                </div>
                <div className="flex min-h-36 items-center justify-center overflow-hidden rounded-2xl border border-dashed border-slate-200 bg-slate-50">
                  {previewImage ? <img src={previewImage} alt="Προεπισκόπηση συνεργάτη" className="h-full min-h-36 w-full object-cover" /> : <div className="px-4 text-center"><Image className="mx-auto h-6 w-6 text-slate-300" /><p className="mt-2 text-xs leading-5 text-slate-500">Η εικόνα θα εμφανιστεί εδώ</p></div>}
                </div>
                {isAdminView && (
                  <div className="flex flex-col gap-2 border-t border-slate-100 pt-4 lg:col-span-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs leading-5 text-slate-500">Η προεπισκόπηση ανοίγει σε νέο tab χωρίς να αποθηκεύει αλλαγές.</p>
                    <Button type="button" variant="outline" className="h-11 shrink-0" disabled={!initialData?.id} onClick={() => initialData?.id && window.open(`/mini-preview/${initialData.id}`, "_blank", "noopener,noreferrer")}>Προβολή mini page</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card className="rounded-2xl border-slate-200 shadow-sm xl:sticky xl:top-5">
            <CardHeader className="p-4 pb-0 sm:p-5 sm:pb-0"><CardTitle className="flex items-center gap-2 text-base text-slate-950"><MapPinned className="h-4 w-4 text-sky-700" /> Περιοχή κάλυψης</CardTitle><p className="text-sm leading-6 text-slate-500">Το σημείο και η ακτίνα καθορίζουν ποια leads μπορούν να ανατεθούν αυτόματα.</p></CardHeader>
            <CardContent className="space-y-5 p-4 sm:p-5">
              <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
                <div ref={mapRef} className="h-[280px] w-full sm:h-[360px]" aria-label="Χάρτης περιοχής κάλυψης" />
                {!mapsLoaded && !mapsError && <div className="absolute inset-0 flex items-center justify-center bg-slate-50/95"><div className="text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-sky-700" /><p className="mt-2 text-sm text-slate-600">Φόρτωση Google Maps…</p></div></div>}
                {mapsError && <div className="absolute inset-0 flex items-center justify-center bg-amber-50 p-6 text-center"><div><MapPin className="mx-auto h-6 w-6 text-amber-700" /><p className="mt-2 text-sm leading-6 text-amber-900">{mapsError}</p></div></div>}
              </div>
              <div className="rounded-xl bg-slate-50 p-3 text-xs leading-5 text-slate-600">
                {selectedLatitude !== null && selectedLatitude !== undefined && selectedLongitude !== null && selectedLongitude !== undefined
                  ? <>Σημείο: <span className="font-semibold tabular-nums text-slate-900">{selectedLatitude.toFixed(5)}, {selectedLongitude.toFixed(5)}</span></>
                  : <>Δεν έχει επιλεγεί ακριβές σημείο. Πατήστε στον χάρτη ή επιλέξτε πρόταση τοποθεσίας.</>}
              </div>
              <FormField control={form.control} name="radius" render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between gap-4"><FormLabel>Ακτίνα εξυπηρέτησης</FormLabel><div className="flex items-center gap-2"><Input className="h-11 w-20 text-right tabular-nums" type="number" min={1} max={100} step={1} value={field.value} onChange={(event) => { const next = Math.min(100, Math.max(1, Number(event.target.value) || 1)); field.onChange(next); updateRadius([next]); }} aria-label="Ακτίνα σε χιλιόμετρα" /><span className="text-sm font-medium text-slate-500">χλμ.</span></div></div>
                  <FormControl><Slider min={1} max={100} step={1} value={[field.value]} onValueChange={(values) => { field.onChange(values[0]); updateRadius(values); }} className="min-h-11 py-4" aria-label="Ακτίνα εξυπηρέτησης" /></FormControl>
                  <p className="text-xs leading-5 text-slate-500">Η αυτόματη ανάθεση δεν ξεπερνά αυτή την ακτίνα.</p>
                  <FormMessage />
                </FormItem>
              )} />
            </CardContent>
          </Card>
        </div>

        <div className="h-14 sm:hidden" aria-hidden="true" />
        <div className="sticky bottom-2 z-20 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-lg backdrop-blur sm:flex-row sm:items-center sm:justify-between sm:p-4">
          <div className="min-w-0">
            <p className={`text-sm font-semibold ${isDirty || isCreate ? "text-amber-800" : "text-emerald-700"}`}>
              {isCreate
                ? (isDirty ? "Η νέα εγγραφή δεν έχει δημιουργηθεί ακόμη" : "Συμπληρώστε τα βασικά στοιχεία")
                : (isDirty ? "Υπάρχουν μη αποθηκευμένες αλλαγές" : "Όλες οι αλλαγές είναι αποθηκευμένες")}
            </p>
            <p className="mt-0.5 text-xs leading-5 text-slate-500">
              {isCreate
                ? "Η δημιουργία δεν αλλάζει ιστορικά leads και δεν στέλνει αυτόματα setup μήνυμα."
                : "Η ενημέρωση επηρεάζει τα επόμενα leads· δεν αλλάζει ιστορικές αναθέσεις."}
            </p>
          </div>
          <Button type="submit" disabled={isSubmitting || (!isDirty && Boolean(initialData?.id))} className="h-12 w-full gap-2 bg-slate-950 px-6 text-white hover:bg-slate-800 sm:w-auto">
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isSubmitting ? "Αποθήκευση…" : submitLabel || (isAdminView ? (initialData?.id ? "Αποθήκευση αλλαγών" : "Προσθήκη συνεργάτη") : "Ολοκλήρωση εγγραφής")}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default PartnerForm;
